import type { MessageGenerationStep } from "@/features/ai-agent/types/agent-step";

/** Which shape of run to seed: a finished one, one parked on an approval, or
 *  one still mid-flight. */
export type RunVariant = "settled" | "needs_approval" | "running";

// Every timestamp is anchored off this fixed instant (and step ids are derived
// from the correlation id) so successive runs - and e2e visual baselines -
// are byte-identical. Nothing in this file may use `faker`'s random surface or
// `Math.random`.
const BASE_AT = Date.parse("2026-06-24T11:30:00.000Z");

const at = (offsetMs: number): string =>
  new Date(BASE_AT + offsetMs).toISOString();

/** The human's approval wait on the settled variant - long enough that
 *  `stepWaitedMs` has something meaningful to render. */
const APPROVAL_WAIT_MS = 4 * 60 * 1000;

const APPROVAL_AT = 3_300;
const RESPONSE_AT = 3_400;

const REPLY_TEXT =
  "Great news: FUE hair transplant starts at ₹85,000 and the consultation is ₹1,000, waived when you book. Dr. Anaya Rao has Thursday 26 Jun at 10:00 free, so I've pencilled you in.";

export const makeAgentRunSteps = (
  correlationId: string,
  variant: RunVariant,
): MessageGenerationStep[] => {
  const stepId = (index: number): string => `${correlationId}-step-${index}`;
  const isSettled = variant === "settled";
  const isRunning = variant === "running";

  // Steps 2 and 3 are one assistant turn (iteration 0) - batch-inserted, hence
  // the shared createdAt - so the timeline has a real group to render.
  const knowledgeSearch: MessageGenerationStep = {
    id: stepId(2),
    type: "execute_tool",
    input: { type: "tool", toolId: "search_knowledge_base" },
    output: null,
    messageId: null,
    generationId: correlationId,
    status: "resolved",
    error: null,
    iteration: 0,
    resolvedByUserId: null,
    resolvedAt: null,
    startedAt: at(700),
    completedAt: at(1_100),
    // Fields and no description: the single-field row that `getStepQuery`
    // renders bare. A description here would never render - a step's record
    // wins over prose restating it (see getStepDetail).
    meta: {
      title:
        'Searched the knowledge base for "hair transplant pricing" and found 2 answers',
      fields: [{ label: "Query", value: "hair transplant pricing" }],
    },
    createdAt: at(700),
  };

  const availability: MessageGenerationStep = {
    id: stepId(3),
    type: "execute_tool",
    input: { type: "tool", toolId: "available_booking_slots" },
    output: null,
    messageId: null,
    generationId: correlationId,
    status: isRunning ? "running" : "resolved",
    error: null,
    iteration: 0,
    resolvedByUserId: null,
    resolvedAt: null,
    startedAt: at(1_100),
    completedAt: isRunning ? null : at(2_000),
    // The pager path needs two settled siblings with an IDENTICAL title and two
    // fields that both differ (see mergeRepeatedThreadSteps + describeMergedCalls).
    // A generic title is the only shape that reaches that branch - the resolver's
    // richer titles name the doctor and so never repeat across calls.
    meta: {
      title: "Checked availability",
      fields: [
        { label: "Doctor", value: "Dr. Anaya Rao" },
        { label: "Window", value: "Thu, 26 Jun – Sat, 28 Jun" },
      ],
    },
    createdAt: at(700),
  };

  // A second availability call, differing from the first by BOTH doctor and
  // window. Two repeated calls that vary in more than one field are the case the
  // thread must not flatten (it would lose which doctor went with which dates),
  // so this is what exercises the step pager. Without it the merge and pager
  // paths never render in mock mode and can't be screenshotted or covered.
  const secondAvailability: MessageGenerationStep = {
    ...availability,
    id: stepId(31),
    input: { type: "tool", toolId: "available_booking_slots" },
    output: null,
    startedAt: at(2_000),
    completedAt: isRunning ? null : at(2_400),
    meta: {
      title: "Checked availability",
      fields: [
        { label: "Doctor", value: "Dr. Meera Iyer" },
        { label: "Window", value: "Mon, 30 Jun – Tue, 1 Jul" },
      ],
    },
  };

  if (isRunning) return [knowledgeSearch, availability];

  const orderHistory: MessageGenerationStep = {
    id: stepId(4),
    type: "execute_tool",
    input: { type: "tool", toolId: "get_order_history" },
    output: null,
    messageId: null,
    generationId: correlationId,
    status: "failed",
    error: "ETIMEDOUT",
    iteration: 1,
    resolvedByUserId: null,
    resolvedAt: null,
    startedAt: at(2_100),
    completedAt: at(3_200),
    meta: { title: "Couldn't fetch the order history" },
    createdAt: at(2_100),
  };

  const approvalStartedAt = APPROVAL_AT + APPROVAL_WAIT_MS;
  const approvalCompletedAt = approvalStartedAt + 900;

  const appointment: MessageGenerationStep = {
    id: stepId(5),
    type: "execute_tool",
    input: { type: "tool", toolId: "create_appointment" },
    output: null,
    messageId: null,
    generationId: correlationId,
    status: isSettled ? "executed" : "waiting",
    error: null,
    iteration: 2,
    resolvedByUserId: isSettled ? "user-1" : null,
    resolvedAt: isSettled ? at(approvalCompletedAt) : null,
    startedAt: isSettled ? at(approvalStartedAt) : null,
    completedAt: isSettled ? at(approvalCompletedAt) : null,
    meta: {
      title: isSettled
        ? "Booked an appointment with Dr. Anaya Rao"
        : "Waiting on approval to book an appointment",
      // The backend's completed write really does send both - a prose line
      // restating the same record. Kept so the timeline's "fields win" rule
      // stays covered: only the fields below should render.
      ...(isSettled
        ? {
            description:
              "FUE Hair Transplant on Thu, 26 Jun 2026 between 10:00 AM and 11:00 AM.",
          }
        : {}),
      requiresApproval: true,
      actionType: "create_appointment",
      fields: [
        { label: "Doctor", value: "Dr. Anaya Rao" },
        { label: "Treatment", value: "FUE Hair Transplant" },
        { label: "Date", value: "Thu, 26 Jun 2026" },
        { label: "Start Time", value: "10:00 AM" },
        { label: "End Time", value: "11:00 AM" },
      ],
    },
    createdAt: at(APPROVAL_AT),
  };

  const reply: MessageGenerationStep = {
    id: stepId(6),
    type: "response",
    input: null,
    output: { text: REPLY_TEXT, media: [], confidence: 0.72 },
    messageId: null,
    generationId: correlationId,
    status: isSettled ? "executed" : "waiting",
    error: null,
    iteration: null,
    resolvedByUserId: isSettled ? "user-1" : null,
    resolvedAt: isSettled ? at(approvalCompletedAt + 100) : null,
    startedAt: null,
    completedAt: null,
    meta: {
      messages: [{ type: "text", text: REPLY_TEXT }],
    },
    createdAt: at(RESPONSE_AT),
  };

  return [
    knowledgeSearch,
    availability,
    secondAvailability,
    orderHistory,
    appointment,
    reply,
  ];
};
